/**
 * FILE: packages/utils/src/jobCompensation.ts
 * PURPOSE: Format job compensation/stipend for display across apps.
 * AGENT_CONTEXT: Pure functions. No React / Supabase client imports.
 * LAST_REVIEWED: 2026-08-12
 */

export interface JobCompensationPick {
  job_type?: string | null
  salary_min?: number | null
  salary_max?: number | null
  compensation_text?: string | null
}

function formatNumericCompensation(job: JobCompensationPick): string | null {
  const { job_type: jobType, salary_min: min, salary_max: max } = job
  if (jobType === 'internship' && min != null) {
    return `₹${min.toLocaleString('en-IN')}/month`
  }
  if (max != null) {
    return `₹${max} LPA`
  }
  return null
}

/** Compact line for job cards. */
export function formatJobCompensationCard(job: JobCompensationPick): string | null {
  if (job.compensation_text?.trim()) return job.compensation_text.trim()
  return formatNumericCompensation(job)
}

/** Labeled line for job detail; null when nothing to show. */
export function formatJobCompensationDetail(
  job: JobCompensationPick
): { label: string; value: string } | null {
  if (job.compensation_text?.trim()) {
    return {
      label: job.job_type === 'internship' ? 'Stipend' : 'Compensation',
      value: job.compensation_text.trim(),
    }
  }

  const numeric = formatNumericCompensation(job)
  if (!numeric) return null
  return {
    label: job.job_type === 'internship' ? 'Stipend' : 'Compensation',
    value: numeric,
  }
}
